import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Display } from '@/components/ui/Typography';
import { friendlyMessage } from '@/lib/errors';
import {
  cardsApi,
  formatCardExpiry,
  formatCardLabel,
  isCardsUnavailable,
  type SavedCard
} from '@/api/cards';

/**
 * T3 — Gestão de cartões (`/wallet/cards`).
 *
 * Design: projeto Claude Design `019dd44a-8ae7-7add-a5a6-d11ade5addd7`,
 * `Bow Carteiras e Cartão.html` → frames T3a (lista) e T3a′ (remoção com
 * confirmação).
 *
 * Cartão serve só pra ABASTECER a carteira — nunca paga a sessão direto.
 * Por isso o subtítulo do cabeçalho é "Pra adicionar saldo": é a única
 * coisa que o cliente faz com um cartão salvo aqui.
 *
 * "Adicionar cartão" NÃO tem formulário próprio: o PagBank só tokeniza o
 * cartão numa cobrança (`store_card` no aporte) — salvar sem cobrar exige a
 * API de Validação e Armazenamento, fora da homologação. O botão leva ao
 * Adicionar saldo com Cartão escolhido e "Salvar" ligado (`intent:
 * 'add_card'`). O antigo frame T3b ficou no ar como rascunho que nunca salvava
 * nada — docs/bugs/2026-10-08-adicionar-cartao-stub.md.
 */

const CHIP_DEFAULT_BG = 'rgba(0,192,114,0.12)';
const CHIP_DEFAULT_FG = '#00875A';
const DANGER_FG = '#D5152B';
const DANGER_BG = 'rgba(238,38,62,0.07)';
const MUTED = 'rgba(0,27,116,0.55)';
const MC_GRADIENT = 'linear-gradient(150deg,#001B74,#22307E)';

/** `location.state` que o Adicionar saldo (RechargePix) lê pra abrir já no trilho de salvar cartão. */
const ADD_CARD_STATE = { from: '/wallet/cards', intent: 'add_card' } as const;

export default function Cards() {
  const navigate = useNavigate();
  const [cards, setCards] = useState<SavedCard[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<SavedCard | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await cardsApi.list();
        if (!cancelled) setCards(data);
      } catch (err) {
        if (cancelled) return;
        // Backend sem a rota (ou flag desligada) não é erro do cliente:
        // a tela vira o estado "em breve", igual ao tile do RechargePix.
        if (isCardsUnavailable(err)) setUnavailable(true);
        else setError(friendlyMessage(err, 'Não conseguimos carregar seus cartões.'));
        setCards([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSetDefault(card: SavedCard) {
    setBusyId(card.id);
    setError(null);
    try {
      await cardsApi.setDefault(card.id);
      setCards((prev) =>
        (prev ?? []).map((c) => ({ ...c, is_default: c.id === card.id }))
      );
    } catch (err) {
      setError(friendlyMessage(err, 'Não conseguimos definir esse cartão como padrão.'));
    } finally {
      setBusyId(null);
    }
  }

  async function handleRemove(card: SavedCard) {
    setBusyId(card.id);
    setError(null);
    try {
      await cardsApi.remove(card.id);
      setCards((prev) => (prev ?? []).filter((c) => c.id !== card.id));
      setRemoving(null);
    } catch (err) {
      setError(friendlyMessage(err, 'Não conseguimos remover esse cartão.'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Screen title="Cartões" subtitle="Pra adicionar saldo" onBack={() => navigate('/wallet')}>
      {unavailable ? (
        <CardsUnavailable />
      ) : (
        <CardsList
          cards={cards}
          busyId={busyId}
          onAdd={() => navigate('/wallet/recharge', { state: ADD_CARD_STATE })}
          onSetDefault={handleSetDefault}
          onRemove={setRemoving}
        />
      )}

      {error && (
        <p className="px-1 text-[12.5px]" style={{ color: DANGER_FG }}>
          {error}
        </p>
      )}

      {removing && (
        <RemoveSheet
          card={removing}
          busy={busyId === removing.id}
          onConfirm={() => handleRemove(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </Screen>
  );
}

/* ───────────────────────── moldura ───────────────────────── */

function Screen({
  title,
  subtitle,
  onBack,
  children
}: {
  title: string;
  subtitle?: string;
  onBack: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={onBack}
          aria-label="Voltar"
          className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-full bg-bow-royal/5 text-bow-royal"
        >
          <ChevronLeftIcon />
        </button>
        <div className="min-w-0 flex-1">
          <Display size={22}>{title}</Display>
          {subtitle && (
            <div className="mt-0.5 text-xs" style={{ color: MUTED }}>
              {subtitle}
            </div>
          )}
        </div>
      </header>
      {children}
    </div>
  );
}

/* ───────────────────────── T3a — lista ───────────────────────── */

interface ListProps {
  cards: SavedCard[] | null;
  busyId: string | null;
  onAdd: () => void;
  onSetDefault: (card: SavedCard) => void;
  onRemove: (card: SavedCard) => void;
}

export function CardsList({ cards, busyId, onAdd, onSetDefault, onRemove }: ListProps) {
  if (cards === null) {
    return (
      <div className="flex flex-col gap-2.5">
        <div className="h-[76px] animate-pulse rounded-[20px] bg-white shadow-soft" />
        <div className="h-[76px] animate-pulse rounded-[20px] bg-white shadow-soft" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {cards.length === 0 ? (
        <EmptyState />
      ) : (
        cards.map((card) => (
          <CardListRow
            key={card.id}
            card={card}
            busy={busyId === card.id}
            onSetDefault={() => onSetDefault(card)}
            onRemove={() => onRemove(card)}
          />
        ))
      )}

      <Button variant="secondary" className="mt-1" onClick={onAdd}>
        <PlusIcon /> Adicionar cartão
      </Button>
      {/* Sem isso o cliente estranha cair no "Adicionar saldo": o cartão só é
          tokenizado numa cobrança, então a 1ª recarga com ele é que o salva. */}
      <p className="px-1 text-[12px] leading-[1.45]" style={{ color: MUTED }}>
        O cartão fica salvo na primeira vez que você adiciona saldo com ele — o valor pago vira
        saldo na sua carteira.
      </p>

      <SecurityNote>
        Cartões salvos de forma tokenizada pelo provedor de pagamento — a Bow não armazena os
        números.
      </SecurityNote>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-[20px] bg-white px-[18px] py-5 shadow-soft">
      <div className="text-[14px] font-semibold text-bow-royal">Nenhum cartão salvo</div>
      <p className="mt-1.5 text-[12.5px] leading-[1.45]" style={{ color: MUTED }}>
        Salve um cartão pra adicionar saldo sem abrir o app do banco. O cashback continua sendo
        benefício do PIX.
      </p>
    </div>
  );
}

export function CardsUnavailable() {
  return (
    <div className="rounded-[20px] bg-white px-[18px] py-5 shadow-soft">
      <div className="text-[14px] font-semibold text-bow-royal">Cartão em breve</div>
      <p className="mt-1.5 text-[12.5px] leading-[1.45]" style={{ color: MUTED }}>
        Ainda estamos habilitando o pagamento por cartão. Por enquanto, o saldo entra por PIX — na
        hora, e com cashback.
      </p>
    </div>
  );
}

function CardListRow({
  card,
  busy,
  onSetDefault,
  onRemove
}: {
  card: SavedCard;
  busy: boolean;
  onSetDefault: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      className="flex items-center gap-3 rounded-[20px] bg-white shadow-soft"
      style={{ padding: '14px 16px', opacity: busy ? 0.6 : 1 }}
    >
      <MiniCardArt brand={card.brand} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-bow-royal">{formatCardLabel(card)}</span>
          {card.is_default && (
            <span
              className="rounded-full text-[9.5px] font-semibold uppercase tracking-[0.04em]"
              style={{ padding: '2.5px 8px', background: CHIP_DEFAULT_BG, color: CHIP_DEFAULT_FG }}
            >
              Padrão
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[11.5px]" style={{ color: MUTED }}>
          Crédito · vence {formatCardExpiry(card)}
        </div>
        {!card.is_default && (
          <button
            type="button"
            onClick={onSetDefault}
            disabled={busy}
            className="mt-1.5 text-xs font-semibold text-bow-royal disabled:opacity-50"
          >
            Definir como padrão
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={busy}
        aria-label={`Remover ${formatCardLabel(card)}`}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl disabled:opacity-50"
        style={{ background: DANGER_BG, color: DANGER_FG }}
      >
        <TrashIcon />
      </button>
    </div>
  );
}

/* ─────────────────── T3a′ — remoção com confirmação ─────────────────── */

interface RemoveSheetProps {
  card: SavedCard;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function RemoveSheet({ card, busy, onConfirm, onCancel }: RemoveSheetProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(2,10,44,0.38)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-t-[28px] bg-bow-cream"
        style={{
          padding: '26px 24px 34px',
          paddingBottom: 'max(34px, env(safe-area-inset-bottom))',
          boxShadow: '0 -10px 30px rgba(0,10,60,0.28)'
        }}
      >
        <div
          aria-hidden
          className="mx-auto mb-[18px] rounded-full"
          style={{ width: 40, height: 4.5, background: 'rgba(0,27,116,0.14)' }}
        />
        <div className="flex items-center gap-3">
          <MiniCardArt brand={card.brand} />
          <Display size={20}>Remover {formatCardLabel(card)}?</Display>
        </div>
        <p className="mt-2.5 text-[13.5px] leading-[1.45]" style={{ color: 'rgba(0,27,116,0.6)' }}>
          Você pode adicionar de novo quando quiser. Suas outras formas de pagamento continuam
          valendo.
        </p>
        <div className="h-5" />
        <Button variant="danger" onClick={onConfirm} disabled={busy}>
          {busy ? 'Removendo…' : 'Remover cartão'}
        </Button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="mt-2 w-full p-3 text-sm font-semibold disabled:opacity-50"
          style={{ color: 'rgba(0,27,116,0.6)' }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

/* ───────────────────────── átomos ───────────────────────── */

function SecurityNote({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 px-1">
      <span className="mt-px flex-shrink-0" style={{ color: 'rgba(0,27,116,0.45)' }}>
        <LockIcon size={13} />
      </span>
      <span className="text-[11.5px] leading-[1.45]" style={{ color: MUTED }}>
        {children}
      </span>
    </div>
  );
}

/**
 * Arte reduzida do cartão. Mastercard e Visa usam as marcas das bandeiras
 * (fonte de sistema de propósito — marca de terceiro não usa Chillax);
 * as demais caem num retângulo navy com a sigla.
 */
function MiniCardArt({ brand }: { brand: string | null }) {
  const isVisa = brand === 'visa';
  return (
    <span
      aria-hidden
      className="flex flex-shrink-0 items-center justify-center"
      style={{
        width: 52,
        height: 34,
        borderRadius: 8,
        background: isVisa ? '#FFFFFF' : MC_GRADIENT,
        border: isVisa ? '1px solid rgba(0,27,116,0.14)' : 'none'
      }}
    >
      <BrandMark brand={brand} />
    </span>
  );
}

function BrandMark({ brand }: { brand: string | null }) {
  if (brand === 'visa') {
    return (
      <span
        style={{
          fontFamily: 'system-ui, sans-serif',
          fontStyle: 'italic',
          fontWeight: 800,
          fontSize: 13,
          letterSpacing: '-0.02em',
          color: '#1A1F71'
        }}
      >
        VISA
      </span>
    );
  }
  if (brand === 'mastercard' || brand === null) {
    return (
      <span className="inline-flex flex-shrink-0">
        <span
          style={{ width: 13, height: 13, borderRadius: 999, background: '#EB001B', opacity: 0.92 }}
        />
        <span
          style={{
            width: 13,
            height: 13,
            borderRadius: 999,
            background: '#F79E1B',
            opacity: 0.92,
            marginLeft: -13 * 0.38
          }}
        />
      </span>
    );
  }
  return (
    <span
      style={{
        fontFamily: 'system-ui, sans-serif',
        fontWeight: 700,
        fontSize: 9,
        letterSpacing: '0.06em',
        color: '#F1EDE4'
      }}
    >
      {brand.slice(0, 5).toUpperCase()}
    </span>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 7h16M9 7V5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5v2M6.5 7l1 13h9l1-13" />
    </svg>
  );
}

function LockIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
